import {
  ActionIcon,
  Button,
  Card,
  Group,
  Modal,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCirclePlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

const Schema = z.object({
  title: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).optional(),
});
type Form = z.infer<typeof Schema>;

/** Contact groups CRUD — the "Groups" tab of the Contacts hub. */
export function GroupsPanel() {
  const list = trpc.contacts.groups.list.useQuery();
  const utils = trpc.useUtils();
  const [opened, { open, close }] = useDisclosure(false);
  // null = create mode; { groupId, ... } = edit mode. The same modal +
  // form serve both — flips title, button label, and which mutation
  // fires on submit. Closing the modal resets to create mode so the
  // next open isn't pre-filled with stale data.
  const [editing, setEditing] = useState<{ groupId: number } | null>(null);
  const create = trpc.contacts.groups.create.useMutation({
    onSuccess: () => {
      void utils.contacts.groups.list.invalidate();
      close();
      notifications.show({ message: 'Group created', color: 'green' });
      form.reset();
    },
  });
  const update = trpc.contacts.groups.update.useMutation({
    onSuccess: () => {
      void utils.contacts.groups.list.invalidate();
      close();
      setEditing(null);
      notifications.show({ message: 'Group updated', color: 'green' });
      form.reset();
    },
  });
  const del = trpc.contacts.groups.delete.useMutation({
    onSuccess: () => void utils.contacts.groups.list.invalidate(),
  });
  const form = useForm<Form>({ resolver: zodResolver(Schema) });
  const startEdit = (g: { id: number; title: string; description: string | null }) => {
    setEditing({ groupId: g.id });
    form.reset({ title: g.title, description: g.description ?? '' });
    open();
  };
  const handleClose = () => {
    setEditing(null);
    form.reset();
    close();
  };

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button leftSection={<IconCirclePlus size={14} />} onClick={open}>
          New group
        </Button>
      </Group>
      <Card p={0}>
        {list.isPending ? (
          <Skeleton height={200} m="md" />
        ) : (
          <Table striped highlightOnHover withRowBorders={false}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Title</Table.Th>
                <Table.Th>Members</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(list.data ?? []).map((g) => (
                <Table.Tr key={g.id}>
                  <Table.Td>{g.title}</Table.Td>
                  <Table.Td>{g._count?.groupContacts ?? 0}</Table.Td>
                  <Table.Td>
                    <Text c="dimmed" size="sm">
                      {g.description ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td align="right">
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      <ActionIcon variant="subtle" onClick={() => startEdit(g)} aria-label="Edit">
                        <IconEdit size={14} />
                      </ActionIcon>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => del.mutate({ groupId: g.id })}
                        aria-label="Delete"
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={editing ? 'Edit group' : 'New group'}
        centered
      >
        <form
          onSubmit={form.handleSubmit((v) => {
            if (editing) update.mutate({ ...v, groupId: editing.groupId });
            else create.mutate(v);
          })}
        >
          <Stack gap="sm">
            <TextInput label="Title" {...form.register('title')} />
            <Textarea label="Description" {...form.register('description')} />
            <Button type="submit" loading={create.isPending || update.isPending}>
              {editing ? 'Save changes' : 'Create'}
            </Button>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
